import { Location } from "@angular/common";
import { Component, OnInit, ChangeDetectorRef } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

import { BaseComponent, UserService, ProductService, SimpleProduct, AdminService, WindowService, EventBusService, ProductSnapshot } from "@mypxplat/xplat/core";
import { AppService } from "@mypxplat/xplat/web/core";
import { AdminAddNewProductComponent } from "../../modals";
import { format } from "date-fns";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "myp-admin-fender-products",
  templateUrl: "fender-products.component.html",
  standalone: false,
})
export class AdminFenderProductsComponent extends BaseComponent implements OnInit {
  public loading: boolean;
  public products: Array<SimpleProduct>;
  public displayedProducts: Array<SimpleProduct>;
  public filteredProducts: Array<SimpleProduct>;
  public showMigrated: boolean = false;
  public sections: any = [
    {
      name: "Fender Products",
      id: "list",
    },
    {
      name: "Missing Serials",
      id: "missing_serials",
      selected: true,
    },
  ];
  public selectedSection;
  public pageOffset = 1;
  public pageLimit = 100;
  public totalPages: number;
  public missingSerials: Array<any>;
  public displayedMissingSerials: Array<any>;
  public nextToken: string | null = null;
  public previousTokens: string[] = [];
  public loadingNext: boolean = false;
  public loadingPrevious: boolean = false;
  private pageCache: { [key: string]: { items: any[]; nextToken: string | null } } = {};
  public metadataMap: { [key: string]: any[] } = {};
  public filters: {
    migrated: "yes" | "no" | "any";
    sku: "yes" | "no" | "any";
  } = {
    migrated: "no",
    sku: "any",
  };

  public queryType: "email" | "userId" | "sku" | "serial" = "email";
  public queryValue: string = "";
  public searching: boolean = false;
  public savingSku: boolean = false;
  public fenderGear: ProductSnapshot;
  public dataSources: Array<{
    source: string;
    api: string;
    endpoint: string;
    data: any;
  }> = [];
  public allSpecs: any[];
  public specCategories: any = {
    accessories: true,
    body: true,
    electronics: true,
    general: true,
    hardware: true,
    measurements: true,
    miscellaneous: true,
    neck: true,
  };
  public title: string;
  public subTitle: string;
  public specsCollapsed: boolean = false;
  public dataSourcesCollapsed: boolean = false;
  public showAllSpecs: boolean = false;
  public snapshotError: string;
  public regeneratingSnapshot: boolean = false;
  constructor(
    userService: UserService,
    public productService: ProductService,
    public adminService: AdminService,
    public appService: AppService,
    private _activatedRoute: ActivatedRoute,
    public router: Router,
    public location: Location,
    private _win: WindowService,
    private _eventbusService: EventBusService,
    private _translate: TranslateService
  ) {
    super(userService);
  }

  ngOnInit() {
    this.loading = true;
    this.selectSection(this.sections[1].id);
  }

  searchFenderGear() {
    this.searching = true;
    this.adminService.searchFenderGear(this.queryType == "sku" ? this.queryValue : undefined, this.queryType == "serial" ? this.queryValue : undefined).subscribe({
      next: (result: any) => {
        this.dataSources = result.dataSources || [];
        if (Array.isArray(result.snapshot)) {
          // todo: handle multiple products found
          this.fenderGear = result.snapshot[0];
        } else {
          this.fenderGear = result.snapshot;
        }
        if (result.snapshotError) {
          this.snapshotError = result.snapshotError;
        }
        this.allSpecs = [];
        if (this.fenderGear.fenderProductSpecs.general?.Series) {
          this.title = this.fenderGear.fenderProductSpecs.general.Series;
          if (this.fenderGear.fenderProductSpecs.body?.["Body Shape"]) this.title += " " + this.fenderGear.fenderProductSpecs.body["Body Shape"];
        }
        if (this.fenderGear.fenderProductSpecs.general?.Color) {
          this.subTitle = this.fenderGear.fenderProductSpecs.general.Color;
          if (this.fenderGear.fenderProductSpecs.body?.["Body Finish"]) this.subTitle += ", " + this.fenderGear.fenderProductSpecs.body["Body Finish"];
        }
        for (var i in this.fenderGear.fenderProductSpecs) {
          if (this.specCategories[i]) {
            this.allSpecs.push(this.fenderGear.fenderProductSpecs[i]);
          }
        }
        this.searching = false;
      },
      error: (error) => {
        this.appService.alert("No Fender gear found with that SKU or serial number.");
        this.searching = false;
      },
      complete: () => {
        this.searching = false;
      },
    });
  }

  search() {
    if (this.queryValue.trim() == "") {
      this.loadMissingSerials();
      return;
    }
    this.searching = true;
    let args: any = {};
    if (this.queryType == "email") {
      args.email = this.queryValue;
    } else if (this.queryType == "userId") {
      args.userId = this.queryValue;
    }
    this.adminService.put(`product/fender-gear/search-missing-serials`, args).subscribe({
      next: (result: any) => {
        if (result?.length) {
          this.missingSerials = result;
          this.displayedMissingSerials = [...this.missingSerials];
        } else {
          this.appService.alert("Either that user was not found or they have not entered any missing serials.");
        }
      },
      error: (error) => {
        this.searching = false;
        this.savingSku = false;
        this.appService.alert("Either that user was not found or they have not entered any missing serials.");
      },
      complete: () => {
        this.searching = false;
        this.savingSku = false;
      },
    });
  }
  regenerateSnapshot() {
    this.regeneratingSnapshot = true;
    this.productService.regenerateSnapshot(this.fenderGear.product.id).subscribe({
      next: (result: any) => {
        this.appService.alert("Snapshot regenerated successfully.");
        this.regeneratingSnapshot = false;
      },
      error: (error) => {
        this.appService.alert("Failed to regenerate snapshot.");
        this.regeneratingSnapshot = false;
      },
    });
  }

  showImage(url) {
    this._eventbusService.emit(this._eventbusService.types.viewFullscreenImage, url);
  }

  saveSku(ms) {
    this.savingSku = true;
    this.adminService
      .put(`product/fender-gear/missing-serials`, {
        sku: ms.sku,
        serial: ms.userId + "#" + ms.serial,
      })
      .subscribe({
        next: (result: any) => {
          this.savingSku = false;
          ms.showingEnterSku = false;
          this.displayedMissingSerials = this.displayedMissingSerials.map((item) => (item.serial == ms.serial ? result : item));
          this.missingSerials = this.missingSerials.map((item) => (item.serial == ms.serial ? result : item));
        },
        error: (error) => {
          this.savingSku = false;
          this.appService.alert("We didn't find a Fender product with that SKU.");
        },
        complete: () => {
          this.savingSku = false;
        },
      });
  }

  deleteMissingSerial(ms) {
    this.appService.confirm("Are you sure you want to remove this gear from this account? This cannot be undone, and the product will be removed from the customer's account.").then((result) => {
      if (result) {
        this.adminService
          .put(`product/fender-gear/delete-missing-serial`, {
            serial: ms.serial,
            userId: ms.userId,
          })
          .subscribe((result) => {
            // remove the item from the list
            this.missingSerials = this.missingSerials.filter((item) => item.serial != ms.serial);
            this.displayedMissingSerials = this.displayedMissingSerials.filter((item) => item.serial != ms.serial);
          });
      }
    });
  }

  selectSection(id, navigate = true) {
    this.selectedSection = id;

    this.sections.forEach((section) => {
      section.selected = false;
      if (section.id == id) section.selected = true;
    });
    this.pageOffset = 1;
    this.nextToken = null;
    this.previousTokens = [];
    this.pageCache = {};
    if (navigate) this.router.navigate(["/products/fender_products", id.toLowerCase()], { replaceUrl: true });
    if (id == "list") {
      this.queryType = "sku";
      this.queryValue = "";
    } else if (id == "missing_serials") {
      this.queryType = "email";
      this.queryValue = "";
      this.loadMissingSerials();
    }
  }

  loadMissingSerials(token?: string | null, changingPageLimit: boolean = false) {
    // Create cache key - use 'first' for null token, otherwise use the token itself
    if (changingPageLimit) {
      this.pageOffset = 1;
      this.nextToken = null;
      this.previousTokens = [];
      this.loading = true;
      this.pageCache = {};
    }
    const cacheKey = token || "first";
    // Check if we have this page cached
    if (this.pageCache[cacheKey]) {
      const cached = this.pageCache[cacheKey];
      this.missingSerials = cached.items;
      this.nextToken = cached.nextToken;
      this.previousTokens[this.pageOffset] = token || null;
      // Reset loading flags immediately for cached pages
      this.loadingNext = false;
      this.loadingPrevious = false;
      this.filter();
      return;
    }

    // Not in cache, fetch from API
    let url = "product/fender-gear/missing-serials";
    const params: string[] = [];

    params.push(`limit=${this.pageLimit}`);
    params.push(`sortDirection=desc`);
    if (token) {
      params.push(`nextToken=${token}`);
    }

    if (params.length > 0) {
      url += `?${params.join("&")}`;
    }

    this.adminService.get(url).subscribe((result: any) => {
      this.loading = false;
      this.loadingNext = false;
      this.loadingPrevious = false;

      if (result && result.items) {
        this.missingSerials = result.items;
        this.nextToken = result.nextToken || null;
        // Store the token that was used to get to this page
        // For page 1, this will be null
        this.previousTokens[this.pageOffset] = token || null;
        // Cache the result
        this.pageCache[cacheKey] = {
          items: [...result.items],
          nextToken: this.nextToken,
        };
        // Create metadata map
        result.items.forEach((item) => {
          this.metadataMap[item.userId + "-" + item.serial] = [
            { title: "Date Added", value: item.created ? format(new Date(item.created), "MM/dd/yyyy") : "Unknown" },
            { title: "Serial Number", value: item.serial },
            { title: "SKU", value: item.sku || "No SKU entered.", valueColor: item.sku ? "green" : "red" },
          ];
          if (item.migrated) {
            this.metadataMap[item.userId + "-" + item.serial].push({ title: "", value: "Migrated" });
          }
        });
        this.filter();
      } else {
        // Fallback for old API format
        this.missingSerials = Array.isArray(result) ? result : [];
        this.nextToken = null;
        this.previousTokens[this.pageOffset] = token || null;
        // Cache the result
        this.pageCache[cacheKey] = {
          items: [...this.missingSerials],
          nextToken: null,
        };
        this.filter();
      }
    });
  }

  paginate = (page: string) => {
    // Always reset both loading flags first to clear any previous state
    this.loadingNext = false;
    this.loadingPrevious = false;

    if (page === "next") {
      if (this.nextToken) {
        // Check if we have this page cached
        this.loadingNext = this.pageCache[this.nextToken] ? false : true;
        // Save the token we're about to use for the next page
        const tokenToUse = this.nextToken;
        this.pageOffset++;
        this.previousTokens[this.pageOffset] = tokenToUse;
        this.loadMissingSerials(tokenToUse);
      }
    } else if (page === "previous") {
      if (this.pageOffset > 1) {
        // Check if we have this page cached
        // Get the token that was used to get to the current page
        const tokenForPreviousPage = this.previousTokens[this.pageOffset - 1] || null;
        this.loadingPrevious = this.pageCache[tokenForPreviousPage] ? false : true;
        this.pageOffset--;
        this.loadMissingSerials(tokenForPreviousPage);
      }
    }
  };

  toggleMigrated() {
    this.showMigrated = !this.showMigrated;
    this.filter();
  }

  filter() {
    let filtered = [...this.missingSerials];
    if (this.filters.migrated == "yes") {
      filtered = [...filtered.filter((ms) => ms.migrated)];
    } else if (this.filters.migrated == "no") {
      filtered = [...filtered.filter((ms) => !ms.migrated)];
    } else {
      filtered = [...filtered];
    }
    if (this.filters.sku == "yes") {
      filtered = [...filtered.filter((ms) => ms.sku)];
    } else if (this.filters.sku == "no") {
      filtered = [...filtered.filter((ms) => !ms.sku)];
    }
    this.displayedMissingSerials = [...filtered];
  }
}
